import React, { useState, useEffect, useRef, useCallback } from 'react';
import NavigationMenu from '../components/NavigationMenu';
import LogoBlanc from '../components/LogoBlanc';

const Phonemes2 = () => {
  // États React
  const [showPopup, setShowPopup] = useState(true);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [screenVisible, setScreenVisible] = useState(false);
  const [activeScreen, setActiveScreen] = useState('');
  const [audioStatus, setAudioStatus] = useState('non-initialisé');
  
  // États pour le lecteur audio
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  
  // État pour les speakers - false = inactif (gain 0), true = actif (gain 1)
  const [speakerStates, setSpeakerStates] = useState({
    leftSpeaker: false,    // track1
    roofSpeaker: false,    // track2  
    rightSpeaker: false,   // track3
    groundSpeaker: false,  // track4
    secondGridSpeaker: false // track5
  });

  // Debug - afficher l'état de la popup
  // console.log('🔍 État showPopup:', showPopup, 'audioInitialized:', audioInitialized);

  // Refs pour les éléments DOM et audio
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [activeSection, setActiveSection] = useState(0);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const audioContextRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // Refs pour les sources audio et gains AVEC SPATIALISATION
  const audioTracksRef = useRef({
    track1: { source: null, gainNode: null, pannerNode: null, buffer: null },
    track2: { source: null, gainNode: null, pannerNode: null, buffer: null },
    track3: { source: null, gainNode: null, pannerNode: null, buffer: null },
    track4: { source: null, gainNode: null, pannerNode: null, buffer: null },
    track5: { source: null, gainNode: null, pannerNode: null, buffer: null }
  });

  // Configuration des pistes audio avec positions spatiales
  const audioConfig = [
    { 
      id: 'track1', 
      file: '/media/Phonemes2_Stereo_Track1.wav', 
      speakerKey: 'leftSpeaker',
      spatialPosition: { x: 0.169, y: 0.376 } // Position relative du speaker gauche
    },
    { 
      id: 'track2', 
      file: '/media/Phonemes2_Stereo_Track2.wav', 
      speakerKey: 'roofSpeaker',
      spatialPosition: { x: 0.4325, y: 0.067 } // Position relative du speaker plafond
    },
    { 
      id: 'track3', 
      file: '/media/Phonemes2_Stereo_Track3.wav', 
      speakerKey: 'rightSpeaker',
      spatialPosition: { x: 0.528, y: 0.374 } // Position relative du speaker droit
    },
    { 
      id: 'track4', 
      file: '/media/Phonemes2_Stereo_Track4.wav', 
      speakerKey: 'groundSpeaker',
      spatialPosition: { x: 0.548, y: 0.645 } // Position relative du speaker sol
    },
    { 
      id: 'track5', 
      file: '/media/Phonemes2_Stereo_Track5.wav', 
      speakerKey: 'secondGridSpeaker',
      spatialPosition: { x: 0.81, y: 0.42, section: 1 } // Speaker section 2
    }
  ];

  // Fonction pour charger un fichier audio
  const loadAudioFile = useCallback(async (url) => {
    const possiblePaths = [
      url,
      url.replace('/media/', '/audio/'),
      url.replace('/media/', '/sounds/'),
      url.replace('Phonemes2_Stereo_', 'phonemes2_stereo_').toLowerCase()
    ];

    for (const path of possiblePaths) {
      try {
        console.log(`🔍 Tentative de chargement: ${path}`);
        const response = await fetch(path);
        
        if (response.ok) {
          const arrayBuffer = await response.arrayBuffer();
          const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
          console.log(`✅ Chargé avec succès: ${path} (durée: ${audioBuffer.duration.toFixed(2)}s)`);
          return audioBuffer;
        }
      } catch (error) {
        console.warn(`⚠️ Échec: ${path} - ${error.message}`);
      }
    }
    
    console.error(`❌ Impossible de charger le fichier audio: ${url}`);
    return null;
  }, []);

  // Fonction pour créer un oscillateur de secours AVEC SPATIALISATION
  const createFallbackOscillator = useCallback((trackId, frequency) => {
    if (!audioContextRef.current) return null;

    const oscillator = audioContextRef.current.createOscillator();
    const gainNode = audioContextRef.current.createGain();
    const pannerNode = audioContextRef.current.createStereoPanner();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, audioContextRef.current.currentTime);
    
    // Chaîne audio avec spatialisation
    oscillator.connect(gainNode);
    gainNode.connect(pannerNode);
    pannerNode.connect(audioContextRef.current.destination);
    
    gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
    pannerNode.pan.setValueAtTime(0, audioContextRef.current.currentTime);
    
    oscillator.start();
    
    console.log(`🔧 Oscillateur de secours créé pour ${trackId} (${frequency}Hz) avec spatialisation`);
    
    return { source: oscillator, gainNode, pannerNode, buffer: null };
  }, []);

  // Fonction d'initialisation audio SYNCHRONISÉE
  const initializeAudio = useCallback(async () => {
    try {
      setAudioStatus('initialisation...');
      console.log("🚀 Début de l'initialisation audio Phonèmes #2");
      
      // Créer le contexte audio
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        
        // Resume du contexte si nécessaire
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }
      }

      // Charger tous les fichiers audio en parallèle
      const loadPromises = audioConfig.map(async (config) => {
        const buffer = await loadAudioFile(config.file);
        return { ...config, buffer };
      });

      const loadResults = await Promise.all(loadPromises);
      
      // Moment de synchronisation : démarrer TOUTES les pistes EN MÊME TEMPS
      const startTime = audioContextRef.current.currentTime + 0.1; // Léger délai pour la synchronisation
      let successCount = 0;
      
      loadResults.forEach((result) => {
        const { id, buffer, speakerKey } = result;
        
        let trackData;
        
        if (buffer) {
          // Fichier audio réel AVEC SPATIALISATION
          const source = audioContextRef.current.createBufferSource();
          const gainNode = audioContextRef.current.createGain();
          const pannerNode = audioContextRef.current.createStereoPanner();
          
          source.buffer = buffer;
          source.loop = true;
          
          // Chaîne audio avec spatialisation : source → gain → panner → destination
          source.connect(gainNode);
          gainNode.connect(pannerNode);
          pannerNode.connect(audioContextRef.current.destination);
          
          // CRUCIAL : Gain à 0 au démarrage
          gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
          pannerNode.pan.setValueAtTime(0, audioContextRef.current.currentTime);
          
          // Démarrage synchronisé
          source.start(startTime);
          
          trackData = { source, gainNode, pannerNode, buffer };
          successCount++;
          
          console.log(`✅ ${id} initialisé avec spatialisation et démarré (synchronisé à ${startTime})`);
        } else {
          // Oscillateur de secours
          const frequencies = { track1: 220, track2: 330, track3: 440, track4: 550, track5: 660 };
          trackData = createFallbackOscillator(id, frequencies[id]);
        }
        
        audioTracksRef.current[id] = trackData;
      });

      setAudioInitialized(true);
      setAudioStatus(`prêt (${successCount}/${audioConfig.length} fichiers chargés)`);
      
      // Récupérer la durée de la première piste pour le lecteur
      const firstTrack = Object.values(audioTracksRef.current).find(track => track.buffer);
      if (firstTrack && firstTrack.buffer) {
        setDuration(firstTrack.buffer.duration);
        setIsPlayerVisible(true); // Afficher le lecteur une fois l'audio initialisé
        
        // Démarrer le suivi du temps de lecture
        startTimeTracking();
      }
      
      console.log(`🎵 Initialisation terminée ! ${successCount} pistes synchronisées`);
      console.log('🔊 Toutes les pistes jouent maintenant en silence (gain = 0)');
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation audio:', error);
      setAudioStatus('erreur');
    }
  }, [audioConfig, loadAudioFile, createFallbackOscillator]);

  // Fonction pour démarrer le suivi du temps de lecture
  const startTimeTracking = useCallback(() => {
    const updateTime = () => {
      if (audioContextRef.current) {
        // Calculer le temps basé sur le contexte audio et la durée
        const contextTime = audioContextRef.current.currentTime;
        
        // Trouver une piste active pour référence, sinon utiliser la première disponible
        const referenceTrack = Object.values(audioTracksRef.current).find(track => track.source && track.buffer);
        
        if (referenceTrack && referenceTrack.buffer) {
          // Le temps de lecture est modulé par la durée pour créer une boucle
          const loopedTime = contextTime % referenceTrack.buffer.duration;
          setCurrentTime(loopedTime);
        }
      }
      animationFrameRef.current = requestAnimationFrame(updateTime);
    };
    
    updateTime();
  }, []);

  // Fonction pour arrêter le suivi du temps
  const stopTimeTracking = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Fonction pour chercher un moment spécifique dans la timeline
  const seekToTime = useCallback((targetTime) => {
    if (!audioContextRef.current || !audioInitialized) return;

    console.log(`⏯️ Recherche vers ${targetTime.toFixed(2)}s`);
    
    // Arrêter toutes les sources actuelles
    Object.values(audioTracksRef.current).forEach(track => {
      if (track.source && track.source.stop) {
        try {
          track.source.stop();
        } catch (e) {
          console.warn('Erreur lors de l\'arrêt de la source:', e);
        }
      }
    });

    // Redémarrer toutes les sources au nouveau moment
    const startTime = audioContextRef.current.currentTime + 0.05; // Petit délai pour la synchronisation
    
    audioConfig.forEach((config) => {
      const trackData = audioTracksRef.current[config.id];
      if (trackData && trackData.buffer) {
        // Créer une nouvelle source 
        const newSource = audioContextRef.current.createBufferSource();
        newSource.buffer = trackData.buffer;
        newSource.loop = true;
        
        // Reconnecter la chaîne audio
        newSource.connect(trackData.gainNode);
        
        // Démarrer au bon moment dans la piste
        newSource.start(startTime, targetTime);
        
        // Mettre à jour la référence
        trackData.source = newSource;
      }
    });

    // Mettre à jour le temps courant immédiatement
    setCurrentTime(targetTime);
    
    // Redémarrer le suivi du temps
    stopTimeTracking();
    setTimeout(() => startTimeTracking(), 100);
  }, [audioInitialized, audioConfig, startTimeTracking, stopTimeTracking]);

  // Fonction de formatage du temps
  const formatTime = useCallback((time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Fonction de calcul de spatialisation - SIMPLIFIÉE AU MAXIMUM
  const updateSpatialAudio = useCallback(() => {
    if (!audioInitialized || !audioContextRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    // Position de scroll actuelle
    const scrollLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;
    const totalScrollWidth = container.scrollWidth;
    
    // Position du "listener" (utilisateur) en pourcentage de la page totale (0 à 1)
    const listenerPosition = scrollLeft / (totalScrollWidth - containerWidth);
    
    // Pour chaque piste active, calculer la spatialisation
    Object.entries(speakerStates).forEach(([speakerKey, isActive]) => {
      if (!isActive) return;

      const audioConf = audioConfig.find(conf => conf.speakerKey === speakerKey);
      if (!audioConf) return;

      const trackData = audioTracksRef.current[audioConf.id];
      if (!trackData || !trackData.pannerNode) return;

      // Position absolue du speaker dans la page (0 à 1)
      let speakerAbsolutePosition;
      
      if (audioConf.spatialPosition.section === 1) {
        speakerAbsolutePosition = 0.5 + (audioConf.spatialPosition.x * 0.5);
      } else {
        speakerAbsolutePosition = audioConf.spatialPosition.x * 0.5;
      }
      
      // Calcul de la différence de position
      const positionDiff = listenerPosition - speakerAbsolutePosition;
      
      // Conversion en valeur de pan (-1 à +1)
      let panValue = -positionDiff * 3;
      panValue = Math.max(-1, Math.min(1, panValue));
      
      // Application fluide du changement de pan
      const now = audioContextRef.current.currentTime;
      trackData.pannerNode.pan.cancelScheduledValues(now);
      trackData.pannerNode.pan.setValueAtTime(trackData.pannerNode.pan.value, now);
      trackData.pannerNode.pan.linearRampToValueAtTime(panValue, now + 0.2);
    });
  }, [speakerStates, audioInitialized, audioConfig]);

  const toggleSpeaker = useCallback((speakerKey) => {
    if (!audioInitialized || !audioContextRef.current) {
      console.warn('Audio non initialisé');
      return;
    }

    const currentState = speakerStates[speakerKey];
    const newState = !currentState;
    
    // Trouver la piste correspondante
    const trackConfig = audioConfig.find(config => config.speakerKey === speakerKey);
    if (!trackConfig) return;

    const trackData = audioTracksRef.current[trackConfig.id];
    if (!trackData || !trackData.gainNode) return;

    // Mettre à jour l'état
    setSpeakerStates(prev => ({
      ...prev,
      [speakerKey]: newState
    }));

    // Transition audio fluide
    const targetGain = newState ? 1 : 0;
    const now = audioContextRef.current.currentTime;
    
    trackData.gainNode.gain.cancelScheduledValues(now);
    trackData.gainNode.gain.setValueAtTime(trackData.gainNode.gain.value, now);
    trackData.gainNode.gain.linearRampToValueAtTime(targetGain, now + 0.8);
    
    console.log(`🔊 ${speakerKey} ${newState ? 'ACTIVÉ' : 'DÉSACTIVÉ'} (${trackConfig.id})`);
  }, [speakerStates, audioInitialized, audioConfig]);

  // Gestion des écrans au hover
  const handleScreenHover = useCallback((screenType, isEntering) => {
    setScreenVisible(isEntering);
    setActiveScreen(isEntering ? screenType : '');
  }, []);

  // Fermeture popup et initialisation
  const handleClosePopup = useCallback(async () => {
    console.log("🚀 Fermeture popup - Initialisation audio...");
    setShowPopup(false);
    await initializeAudio();
  }, [initializeAudio]);

  // Effects
  useEffect(() => {
    // S'assurer que la popup s'affiche au montage
    setShowPopup(true);
    setAudioStatus('en attente d\'interaction utilisateur');
  }, []);

  // Navigation par section
  const navigateToSection = useCallback((index) => {
    const container = containerRef.current;
    if (container) {
      container.scrollLeft = index * window.innerWidth;
      setActiveSection(index);
    }
  }, []);

  // Scroll wheel avec ancrages — identique à ChambreEcoute1
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container || isScrollingRef.current) return;

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = true;

      const sectionWidth = window.innerWidth;
      const currentScroll = container.scrollLeft;
      const direction = e.deltaY > 0 ? 1 : -1;

      // Ancrages : une par section (2 sections)
      const anchors = [0, sectionWidth];

      let targetAnchor;
      if (direction > 0) {
        targetAnchor = anchors.find(a => a > currentScroll + 10) ?? anchors[anchors.length - 1];
      } else {
        targetAnchor = [...anchors].reverse().find(a => a < currentScroll - 10) ?? 0;
      }

      const maxScroll = container.scrollWidth - container.clientWidth;
      targetAnchor = Math.max(0, Math.min(maxScroll, targetAnchor));

      // Mettre à jour la puce active
      setActiveSection(Math.round(targetAnchor / sectionWidth));

      // Animation mouvement rapide + freinage final
      const startTime = performance.now();
      const startScroll = container.scrollLeft;
      const distance = targetAnchor - startScroll;
      const duration = 140;

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        let easeProgress;
        if (progress < 0.7) {
          easeProgress = progress / 0.7;
        } else {
          const fp = (progress - 0.7) / 0.3;
          easeProgress = 1 - Math.pow(1 - fp, 3) * 0.1;
        }
        container.scrollLeft = startScroll + distance * easeProgress;
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          isScrollingRef.current = false;
        }
      };

      requestAnimationFrame(animateScroll);
    }, 3);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [handleWheel, isMobile]);

  // SPATIALISATION COMPLÈTEMENT SÉPARÉE - SEULEMENT quand on s'arrête
  useEffect(() => {
    let spatialTimeout;
    
    const handleSpatialUpdate = () => {
      clearTimeout(spatialTimeout);
      spatialTimeout = setTimeout(() => {
        updateSpatialAudio();
      }, 200); // Attendre 200ms après l'arrêt du scroll
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleSpatialUpdate, { passive: true });
      
      return () => {
        container.removeEventListener('scroll', handleSpatialUpdate);
        clearTimeout(spatialTimeout);
      };
    }
  }, [updateSpatialAudio]);

  // Cleanup
  useEffect(() => {
    return () => {
      // Arrêter le suivi du temps
      stopTimeTracking();
      
      // Nettoyer les sources audio
      Object.values(audioTracksRef.current).forEach(track => {
        if (track.source && track.source.stop) {
          try {
            track.source.stop();
          } catch (e) {
            console.warn('Erreur lors de l\'arrêt de la source audio:', e);
          }
        }
      });
      
      // Fermer le contexte audio
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, [stopTimeTracking]);

  return (
    <>
      {/* Styles CSS intégrés pour éviter les conflits */}
      <style>{`
        .phonemes2-container {
          width: 100%;
          height: 100vh;
          overflow: hidden;
          position: relative;
          background-color: #9697f7;
          font-family: "Roboto", sans-serif;
        }

        .horizontal-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          display: flex;
          overflow-x: auto;
          overflow-y: hidden;
          height: 100vh;
          -ms-overflow-style: none;
          scrollbar-width: none;
          /* SUPPRESSION de scroll-behavior pour éviter les conflits */
        }

        .horizontal-container::-webkit-scrollbar {
          display: none;
        }

        .horizontal-container section {
          min-width: 100%;
          height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          background: #9697f7;
        }

        .first-grid {
          margin-left: 3.5vw;
          margin-top: 7vh;
          width: 90vw;
          height: 86vh;
          display: grid;
          grid-template-columns: 28% 72%;
          grid-template-rows: 1fr;
          grid-template-areas: "a b";
          grid-column-gap: 1vw;
        }

        .first-grid-bloc:nth-child(1) {
          grid-area: a;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding-top: 2vh;
        }

        .first-grid-bloc:nth-child(2) {
          background-image: url("/media/phonemes2pousch2.jpg");
          background-size: contain;
          background-position: top left;
          background-repeat: no-repeat;
          grid-area: b;
          position: relative;
        }

        .second-grid {
          margin-left: 0vw;
          margin-right: 2.5vw;
          margin-top: 7vh;
          width: 92vw;
          height: 86vh;
          display: grid;
          grid-template-columns: 67% 33%;
          grid-template-rows: 1fr;
          grid-template-areas: "a b";
          grid-column-gap: 2.5vw;
        }

        .second-grid-bloc:nth-child(1) {
          background-image: url("/media/phonemes2pousch3.jpg");
          background-size: contain;
          background-position: top left;
          background-repeat: no-repeat;
          grid-area: a;
          position: relative;
        }

        .second-grid-bloc:nth-child(2) {
          background-image: url("/media/phonemes2pousch1.jpg");
          background-size: contain;
          background-position: top left;
          background-repeat: no-repeat;
          grid-area: b;
        }

        .text-bloc:nth-child(1) {
          background-color: #7374ea;
          width: 83%;
          max-height: 23%;
          padding: 5%;
          margin-bottom: 2vh;
          border-radius: 4%;
          transition: all 3s ease-in-out;
          align-items: center;
          justify-content: center;
        }

        .text-bloc:nth-child(2),
        .text-bloc:nth-child(3) {
          margin-top: 3.5%;
          width: 83%;
          transition: all 3s ease-in-out;
        }

        .title {
          font-family: "Roboto", sans-serif;
          font-size: clamp(1.2rem, 1vw, 1.6rem);
          margin-bottom: 4%;
          font-weight: lighter;
          color: white;
          letter-spacing: 0.1rem;
          opacity: 0.85;
        }

        .def {
          font-family: "Roboto", sans-serif;
          font-size: clamp(1.2rem, 1vw, 1.6rem);
          margin-bottom: 4%;
          font-weight: 200;
          color: white;
          letter-spacing: 0.1rem;
          opacity: 0.85;
        }

        .def2 {
          text-align: right;
          color: #2e00bc;
          opacity: 1;
        }

        .text {
          font-family: "Roboto", sans-serif;
          font-size: clamp(1.2rem, 1vw, 1.6rem);
          font-weight: 400;
          color: black;
          letter-spacing: 0.1rem;
          opacity: 0.85;
        }

        .italic {
          font-style: italic;
        }

        #link {
          color: #3f41f5;
          text-decoration: none;
          font-style: italic;
          font-weight: 400;
          transition: 0.4s;
        }

        #link:hover {
          color: #701be3;
          font-style: italic;
          font-weight: 500;
          transition: 0.4s;
        }

        /* Speakers - ÉTATS VISUELS AMÉLIORÉS */
        .speaker {
          position: absolute;
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid transparent;
          border-radius: 8px;
          backdrop-filter: blur(4px);
          opacity: 0.3;
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          z-index: 10;
          transform: scale(1);
        }

        /* État hover */
        .speaker:hover {
          opacity: 0.7;
          background: rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.4);
          transform: scale(1.05);
          box-shadow: 0 8px 25px rgba(255, 255, 255, 0.2);
        }

        /* État actif avec effet de lueur */
        .speaker.active {
          opacity: 1;
          background: rgba(76, 135, 224, 0.3);
          border-color: rgba(74, 154, 247, 0.8);
          transform: scale(1.1);
          box-shadow: 
            0 0 20px rgba(81, 193, 245, 0.6),
            0 0 40px rgba(81, 191, 255, 0.4),
            0 0 60px rgba(71, 180, 252, 0.2);
        }

        /* État actif + hover */
        .speaker.active:hover {
          box-shadow: 
            0 0 25px rgba(40, 212, 198, 0.8),
            0 0 50px rgba(40, 212, 198, 0.6),
            0 0 75px rgba(40, 212, 198, 0.4);
        }

        /* Positions des speakers */
        #left-speaker {
          height: 8.05rem;
          width: 5.25rem;
          left: 16.7%;
          top: 37.6%;
        }

        #roof-speaker {
          height: 5.9rem;
          width: 5.2rem;
          left: 42.2%;
          top: 7.2%;
        }

        #right-speaker {
          height: 4rem;
          width: 3.5rem;
          left: 51.9%;
          top: 37.6%;
        }

        #ground-speaker {
          height: 9.9rem;
          width: 7.2rem;
          left: 53.5%;
          top: 64.3%;
        }

        #second-grid-speaker {
          width: 7rem;
          height: 6.8rem;
          left: 79.8%;
          top: 40.6%;
        }

        /* Écrans interactifs */
        .screen-area {
          position: absolute;
          cursor: cell;
          opacity: 0;
          z-index: 5;
          transition: opacity 0.3s ease;
        }

        .screen-area:hover {
          opacity: 0.1;
          background-color: rgba(255, 255, 255, 0.1);
        }

        #first-grid-left-tablet {
          width: 8.8rem;
          height: 11rem;
          left: 5.65%;
          top: 34%;
          clip-path: polygon(19% 0, 69% 7%, 69% 89%, 19% 87%);
        }

        #first-grid-television {
          width: 9.2rem;
          height: 7.2rem;
          left: 30.24%;
          top: 61.1%;
          clip-path: polygon(0 9%, 88% 0, 98% 11%, 98% 92%, 6% 100%, 0 90%, 0 52%);
        }

        #first-grid-big-screen {
          width: 33.5rem;
          height: 48rem;
          left: 49%;
          top: 20.3%;
          clip-path: polygon(53% 13%, 77% 0, 81% 100%, 56% 85%);
        }

        #first-grid-right-tablet {
          width: 7.9rem;
          height: 18.1rem;
          left: 84%;
          top: 21.7%;
          clip-path: polygon(0 16%, 96% 0, 100% 97%, 2% 100%);
        }

        /* Projections d'écrans */
        #screen {
          position: absolute;
          width: 23%;
          height: 80%;
          visibility: hidden;
          opacity: 0;
          transition: all 1s ease;
          z-index: 15;
          top: 12%;
          left: 7.8%;
        }

        .screen-projection {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 2%;
          visibility: hidden;
          opacity: 0;
          transition: all 1s ease;
        }

        .screen-projection-television {
          position: absolute;
          width: 100%;
          height: 40%;
          top: 27.5%;
          border-radius: 2%;
          visibility: hidden;
          opacity: 0;
          transition: all 1s ease;
        }

        /* Projections section 2 */
        #second-grid-big-screen-projection {
          position: absolute;
          left: 16.2%;
          top: 17.4%;
          height: 51.7rem;
          width: 29.4rem;
          clip-path: polygon(0 0, 100% 0.5%, 100% 99%, 0 100%);
          opacity: 0;
          transition: all 0.5s ease-in-out;
          cursor: cell;
        }

        #second-grid-big-screen-projection:hover {
          opacity: 0.9;
        }

        #second-grid-tablet-projection {
          position: absolute;
          left: 64.6%;
          top: 33.9%;
          height: 6.9rem;
          width: 5.2rem;
          clip-path: polygon(0 0, 100% 0.5%, 100% 99%, 0 99%);
          opacity: 0;
          transition: all 0.5s ease-in-out;
          cursor: cell;
        }

        #second-grid-tablet-projection:hover {
          opacity: 0.9;
        }

        #second-grid-television-projection {
          position: absolute;
          left: 175.5%;
          top: 43.85%;
          height: 15.6rem;
          width: 19.4rem;
          clip-path: polygon(
            2.5% 7%, 2.5% 6%, 4% 5%, 33% 4%, 66% 4%, 94% 5.5%, 97% 7%, 98% 9%,
            99% 33%, 99% 66%, 98% 90%, 96% 92%, 94% 94%, 66% 96%, 33% 96%,
            8% 95%, 5% 94.5%, 2.5% 93%, 2% 66%, 2% 33%
          );
          opacity: 0;
          transition: all 0.5s ease-in-out;
          cursor: cell;
        }

        #second-grid-television-projection:hover {
          opacity: 1;
        }

        /* Lecteur audio flottant */
        .audio-player {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 300px;
          height: 80px;
          background: rgba(40, 212, 198, 0.1);
          backdrop-filter: blur(15px);
          border: 1px solid rgba(40, 212, 198, 0.3);
          border-radius: 15px;
          padding: 15px;
          z-index: 100;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          opacity: 0;
          transform: translateY(20px) scale(0.95);
        }

        .audio-player.visible {
          opacity: 1;
          transform: translateY(0) scale(1);
        }

        .audio-player:hover {
          background: rgba(40, 212, 198, 0.15);
          border-color: rgba(119, 104, 255, 0.8);
          box-shadow: 0 8px 25px rgba(40, 212, 198, 0.2);
          transform: scale(1.02);
        }

        .player-timeline {
          position: relative;
          width: 100%;
          height: 6px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
          margin: 10px 0;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .player-timeline:hover {
          height: 8px;
          background: rgba(255, 255, 255, 0.3);
        }

        .timeline-progress {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background: linear-gradient(90deg, rgba(40, 135, 212, 0.8), rgb(102, 86, 253));
          border-radius: 3px;
          transition: all 0.1s ease;
        }

        .timeline-handle {
          position: absolute;
          top: 50%;
          right: -6px;
          width: 12px;
          height: 12px;
          background: rgba(40, 135, 212, 0.8);
          border: 2px solid rgba(255, 255, 255, 0.8);
          border-radius: 50%;
          transform: translateY(-50%);
          cursor: grab;
          transition: all 0.3s ease;
          opacity: 0;
        }

        .player-timeline:hover .timeline-handle {
          opacity: 1;
        }

        .timeline-handle:hover {
          transform: translateY(-50%) scale(1.2);
          box-shadow: 0 0 15px rgba(40, 135, 212, 0.8);
        }

        .timeline-handle:active {
          cursor: grabbing;
          transform: translateY(-50%) scale(1.3);
        }

        .player-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-family: "Roboto", sans-serif;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 300;
          letter-spacing: 0.5px;
        }

        .player-title {
          font-size: 15px;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 5px;
        }

        .time-display {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          color: rgba(40, 212, 198, 0.9);
        }

        /* Responsive */
        @media (max-width: 991px) {
          .horizontal-container {
            position: absolute;
            display: flex;
            flex-direction: column;
            height: auto;
            transform: none !important;
          }
          
          .first-grid, .second-grid {
            grid-template-columns: 1fr;
            height: auto;
          }
        }

        @media (max-width: 768px) {
          .speaker {
            transform: scale(1.2);
          }
          
          .audio-player {
            width: 280px;
            height: 75px;
            bottom: 15px;
            right: 15px;
            padding: 12px;
          }
          
          .player-title {
            font-size: 13px;
          }
          
          .player-info {
            font-size: 11px;
          }
        }

        @media (max-width: 480px) {
          .audio-player {
            width: 250px;
            height: 70px;
            bottom: 10px;
            right: 10px;
            padding: 10px;
          }
        }

        /* ══════════════════════════════════════════════════════
           RESPONSIVE ÉLÉMENTS INTERACTIFS
           Valeurs de référence (≥1920px) :
           #left-speaker       h:8.05rem  w:5.25rem  left:11.05%  top:16.35%
           #roof-speaker       h:5.9rem   w:5.2rem   left:28%     top:3.22%
           #right-speaker      h:4rem     w:3.5rem   left:34.3%   top:16.25%
           #ground-speaker     h:9.9rem   w:7.2rem   left:35.42%  top:27.8%
           #second-grid-speaker           h:6.8rem   w:7rem       left:50.15%  top:18.18%
           #first-grid-left-tablet        w:8.8rem   h:11rem      left:3.6%    top:14.85%
           #first-grid-television         w:9.2rem   h:7.2rem     left:19.54%  top:26.5%
           #first-grid-big-screen         w:33.5rem  h:48rem      left:32.68%  top:9.22%
           #first-grid-right-tablet       w:7.9rem   h:18.1rem    left:55.7%   top:9.75%
           #second-grid-big-screen-projection  h:53.4rem  w:30.5rem  left:9.15%   top:7.9%
           #second-grid-tablet-projection      h:7.25rem  w:5.45rem  left:40.23%  top:15.08%
           #second-grid-television-projection  h:15.6rem  w:19.4rem  left:9.6%    top:16.72%
        ══════════════════════════════════════════════════════ */

        /* ── 1620px ── */
        @media (max-width: 1620px) {
          #left-speaker       { height: ; width: ; left: ; top: ; }
          #roof-speaker       { height: ; width: ; left: ; top: ; }
          #right-speaker      { height: ; width: ; left: ; top: ; }
          #ground-speaker     { height: ; width: ; left: ; top: ; }
          #second-grid-speaker              { height: ; width: ; left: ; top: ; }
          #first-grid-left-tablet           { width: ; height: ; left: ; top: ; }
          #first-grid-television            { width: ; height: ; left: ; top: ; }
          #first-grid-big-screen            { width: ; height: ; left: ; top: ; }
          #first-grid-right-tablet          { width: ; height: ; left: ; top: ; }
          #second-grid-big-screen-projection  { height: ; width: ; left: ; top: ; }
          #second-grid-tablet-projection      { height: ; width: ; left: ; top: ; }
          #second-grid-television-projection  { height: ; width: ; left: ; top: ; }
        }

        /* ── 1320px ── */
        @media (max-width: 1320px) {
          #left-speaker       { height: ; width: ; left: ; top: ; }
          #roof-speaker       { height: ; width: ; left: ; top: ; }
          #right-speaker      { height: ; width: ; left: ; top: ; }
          #ground-speaker     { height: ; width: ; left: ; top: ; }
          #second-grid-speaker              { height: ; width: ; left: ; top: ; }
          #first-grid-left-tablet           { width: ; height: ; left: ; top: ; }
          #first-grid-television            { width: ; height: ; left: ; top: ; }
          #first-grid-big-screen            { width: ; height: ; left: ; top: ; }
          #first-grid-right-tablet          { width: ; height: ; left: ; top: ; }
          #second-grid-big-screen-projection  { height: ; width: ; left: ; top: ; }
          #second-grid-tablet-projection      { height: ; width: ; left: ; top: ; }
          #second-grid-television-projection  { height: ; width: ; left: ; top: ; }
        }

        /* ── 1024px — dernier breakpoint desktop ── */
        @media (max-width: 1024px) {
          #left-speaker       { height: ; width: ; left: ; top: ; }
          #roof-speaker       { height: ; width: ; left: ; top: ; }
          #right-speaker      { height: ; width: ; left: ; top: ; }
          #ground-speaker     { height: ; width: ; left: ; top: ; }
          #second-grid-speaker              { height: ; width: ; left: ; top: ; }
          #first-grid-left-tablet           { width: ; height: ; left: ; top: ; }
          #first-grid-television            { width: ; height: ; left: ; top: ; }
          #first-grid-big-screen            { width: ; height: ; left: ; top: ; }
          #first-grid-right-tablet          { width: ; height: ; left: ; top: ; }
          #second-grid-big-screen-projection  { height: ; width: ; left: ; top: ; }
          #second-grid-tablet-projection      { height: ; width: ; left: ; top: ; }
          #second-grid-television-projection  { height: ; width: ; left: ; top: ; }
        }
      `}</style>

      {/* Navigation et Logo */}
      <NavigationMenu />
      <LogoBlanc />
      <h1 style={{
        position: 'fixed',
        top: '2.7rem',
        right: '60px',
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: 'clamp(2.5rem, 4vw, 3rem)',
        fontWeight: 300,
        color: 'rgba(255,255,255,0.55)',
        letterSpacing: '0.2rem',
        textAlign: 'right',
        textTransform: 'uppercase',
        margin: 0,
        pointerEvents: 'none',
        zIndex: 1000,
        lineHeight: 1,
      }}>Phonèmes #2</h1>

      {/* Popup d'autorisation audio - CENTRÉE */}
      {showPopup && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(54, 53, 126, 0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div 
            style={{
              width: '90%',
              maxWidth: '600px',
              padding: '40px',
              backgroundColor: 'rgba(54, 53, 126, 0.98)',
              borderRadius: '15px',
              border: '1px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              color: 'aliceblue',
              fontSize: '1.8rem',
              letterSpacing: '0.1rem',
              // textAlign: 'center',
              fontFamily: '"Roboto", sans-serif',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <h2 style={{ marginBottom: '1.5rem', fontSize: '2.2rem' }}>
              Phonèmes #2 - Page interactive
            </h2>
            
            <p style={{ marginBottom: '1rem', fontSize: '1.2rem', lineHeight: '1.5' }}>
              Cette page utilise un système audio multi-pistes synchronisé.
            </p>
            
            <p style={{ marginBottom: '1.5rem', fontSize: '1.2rem', lineHeight: '1.5' , justifyContent: 'flex-start', textAlign: '-khtml-left'}}>
              Cliquez sur les haut-parleurs placés au sein de l'installation pour activer les différentes pistess sonores.
              Survolez les écrans pour visualiser les vidéos.
              Faites défiler horizontalement pour découvrir la spatialisation audio.
            </p>
            
            <div style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <p style={{ marginBottom: '0.8rem', fontSize: '1.5rem', fontWeight: 'bold' }}>
                Écoute au casque conseillée
              </p>
              <img
                src="/media/casque.jpg"
                alt="casque audio"
                style={{ width: '100px', height: 'auto', borderRadius: '6px', opacity: 0.85, mixBlendMode: 'luminosity' }}
              />
            </div>
            
            <button 
              onClick={handleClosePopup}
              style={{
                backgroundColor: 'rgba(40, 212, 198, 0.8)',
                color: 'white',
                border: 'none',
                padding: '15px 30px',
                fontSize: '1.3rem',
                borderRadius: '25px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontFamily: '"Roboto", sans-serif',
                letterSpacing: '0.1rem',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                textAlign: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(40, 212, 198, 1)';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(40, 212, 198, 0.8)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              Démarrer l'expérience sonore
            </button>
          </div>
        </div>
      )}

      {/* Lecteur audio flottant */}
      {isPlayerVisible && !isMobile && (
        <div className={`audio-player ${audioInitialized ? 'visible' : ''}`}>
          <div className="player-title">Phonèmes #2 - Timeline</div>
          
          <div 
            className="player-timeline"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              const percentage = clickX / rect.width;
              const targetTime = percentage * duration;
              seekToTime(targetTime);
            }}
          >
            <div 
              className="timeline-progress"
              style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
            >
              <div className="timeline-handle" />
            </div>
          </div>
          
          <div className="player-info">
            <span className="time-display">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <span style={{ fontSize: '10px', opacity: 0.6 }}>
              {Object.values(speakerStates).filter(Boolean).length} speaker{Object.values(speakerStates).filter(Boolean).length !== 1 ? 's' : ''} actif{Object.values(speakerStates).filter(Boolean).length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}

      {/* Puces de navigation — desktop uniquement */}
      {!isMobile && <div style={{
        position: 'fixed', bottom: '2.5rem', left: '50%',
        transform: 'translateX(-50%)', display: 'flex',
        gap: '4rem', zIndex: 500, alignItems: 'center'
      }}>
        {[0, 1].map(index => (
          <div
            key={index}
            onClick={() => navigateToSection(index)}
            style={{
              clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
              width: '10px', height: '10px',
              backgroundColor: activeSection === index ? 'rgb(230, 228, 255)' : 'rgb(180, 178, 240)',
              opacity: activeSection === index ? 1 : 0.6,
              cursor: 'pointer',
              transition: 'all 0.4s ease-out',
              transform: activeSection === index ? 'scale(1.8)' : 'scale(1)',
            }}
            onMouseEnter={e => { if (activeSection !== index) { e.target.style.backgroundColor = 'rgb(230, 228, 255)'; e.target.style.opacity = '0.9'; }}}
            onMouseLeave={e => { if (activeSection !== index) { e.target.style.backgroundColor = 'rgb(180, 178, 240)'; e.target.style.opacity = '0.6'; }}}
          />
        ))}
      </div>}

      {/* Container principal */}
      <div className="phonemes2-container">
      {isMobile ? (
        <>
          {showPopup && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(54,53,126,0.97)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: '85%', maxWidth: '460px', padding: '2.5rem', backgroundColor: 'rgba(54,53,126,0.98)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', color: 'aliceblue', textAlign: 'center', fontFamily: '"Roboto", sans-serif' }}>
                <p style={{ fontSize: '1.5rem', lineHeight: 1.7, marginBottom: '2rem', fontWeight: 300 }}>Visitez ce site sur un ordinateur pour une expérience immersive et interactive</p>
                <button onClick={() => setShowPopup(false)} style={{ backgroundColor: 'rgba(40,212,198,0.8)', color: 'white', border: 'none', padding: '12px 28px', fontSize: '1.2rem', borderRadius: '25px', cursor: 'pointer', fontFamily: '"Roboto", sans-serif', letterSpacing: '0.1rem', textTransform: 'uppercase' }}>Continuer</button>
              </div>
            </div>
          )}
          <div style={{ position: 'fixed', inset: 0, overflowY: 'scroll', overflowX: 'hidden', background: '#9697f7', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
            <div style={{ padding: '100px 6vw 2rem', fontFamily: '"Roboto", sans-serif' }}>
              <div style={{ marginBottom: '2rem', background: '#7374ea', borderRadius: '8px', padding: '5%' }}>
                <p style={{ fontSize: '1.6rem', fontWeight: 300, color: 'white', letterSpacing: '0.1rem', marginBottom: '0.8rem' }}>Phonème :</p>
                <p style={{ fontSize: '1.5rem', fontWeight: 200, color: 'white', marginBottom: '0.6rem', lineHeight: 1.5 }}>micro unité distinctive du langage que l'on peut combiner ou isoler par segmentation</p>
                <p style={{ fontSize: '1.5rem', fontWeight: 200, color: '#2e00bc', textAlign: 'right', lineHeight: 1.5 }}>- entité sonore pouvant se muer, muter, fusionner en un essaim</p>
              </div>
              <p style={{ fontSize: '1.5rem', fontWeight: 400, lineHeight: 1.7, marginBottom: '2rem', color: 'black' }}>
                &nbsp;&nbsp;&nbsp;Ce deuxième laboratoire immersif de la série <span style={{ fontStyle: 'italic' }}>Phonèmes</span> prenait place dans les locaux de{' '}
                <a href="https://www.poush-manifesto.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#3f41f5', textDecoration: 'none', fontStyle: 'italic' }}>Poush Manifesto</a>{' '}
                en juin et juillet 2021 à Paris à l'occasion de l'exposition <span style={{ fontStyle: 'italic' }}>Félicità – Goodbye Horses</span> organisée par les Beaux Arts de Paris pour les félicités de 2019 et 2020.
              </p>
            </div>
            <div style={{ margin: '0 4vw 2rem', aspectRatio: '3/2', background: '#9697f7 url(/media/phonemes2pousch2.jpg) top center / contain no-repeat', borderRadius: '4px' }} />
            <div style={{ padding: '0 6vw 2rem', fontFamily: '"Roboto", sans-serif', color: 'black' }}>
              <p style={{ fontSize: '1.5rem', fontWeight: 400, lineHeight: 1.7 }}>
                Cette installation fait suite à{' '}
                <a href="/phonemes1" target="_blank" rel="noopener noreferrer" style={{ color: '#3f41f5', textDecoration: 'none', fontStyle: 'italic' }}>Phonèmes#1</a>
                , revisitée pour ce petit espace qui se détachait des autres salles de l'exposition par son atmosphère sonore et colorée. Le visiteur passe la porte, le temps se suspend. Il bascule alors dans milieu affecté par des flux et modulations, des cycles de lumières et de vibrations, une matière qui voyage dans les murs.
                <br /><br />
                Le dispositif est composé d'un grand écran vertical, une télévision cathodique, deux petits écrans verticaux et d'un système son 5.1 permettant aux phonèmes de naviguer dans cinq hauts parleurs. Les fréquences basses font frémir et bruire le sol écorché. Aucun traitement n'est appliqué sur les photographies ({' '}
                <a href="https://www.2angles.org/" target="_blank" rel="noopener noreferrer" style={{ color: '#3f41f5', textDecoration: 'none', fontStyle: 'italic' }}>© Nicolas Brasseur</a>
                ) ; des gélatines colorées ont été utilisées pour filtrer la lumière et envelopper l'espace d'un bleu éthéré qui invite le visiteur dans un autre plan.
              </p>
            </div>
            <div style={{ margin: '0 4vw 2rem', aspectRatio: '3/2', background: '#9697f7 url(/media/phonemes2pousch3.jpg) top center / contain no-repeat', borderRadius: '4px' }} />
            <div style={{ margin: '0 4vw 4rem', aspectRatio: '3/2', background: '#9697f7 url(/media/phonemes2pousch1.jpg) top center / contain no-repeat', borderRadius: '4px' }} />
          </div>
        </>
      ) : (
        <div ref={containerRef} className="horizontal-container">
          
          {/* Section 1 */}
          <section>
            <div className="first-grid">
              
              {/* Bloc de texte */}
              <div className="first-grid-bloc">
                <div className="text-bloc">
                  <p className="title">Phonème :</p>
                  <p className="def">
                    micro unité distinctive du langage que l'on peut combiner ou isoler <br />
                    par segmentation
                  </p>
                  <p className="def def2">- entité sonore pouvant se muer, muter, fusionner en un essaim</p>
                </div>
                
                <div className="text-bloc">
                  <p className="text">
                    &nbsp;&nbsp;&nbsp;Ce deuxième laboratoire immersif de la série <span className="italic">Phonèmes</span> prenait place dans les locaux de{' '}
                    <a id="link" href="https://www.poush-manifesto.com/" target="_blank" rel="noopener noreferrer">
                      Poush Manifesto
                    </a>{' '}
                    en juin et juillet 2021 à Paris à l'occasion de l'exposition <span className="italic">Félicità – Goodbye Horses</span> organisée par les Beaux Arts de Paris pour les félicités de 2019 et 2020.
                    <br /><br />
                    Cette installation fait suite à{' '}
                    <a id="link" href="/phonemes1" target="_blank" rel="noopener noreferrer">
                      Phonèmes#1
                    </a>
                    , revisitée pour ce petit espace qui se détachait des autres salles de l'exposition par son atmosphère sonore et colorée. Le visiteur passe la porte, le temps se suspend. Il bascule alors dans milieu affecté par des flux et modulations, des cycles de lumières et de vibrations, une matière qui voyage dans les murs.
                  </p>
                </div>
                
                <div className="text-bloc">
                  <p className="text">
                    Le dispositif est composé d'un grand écran vertical, une télévision cathodique, deux petits écrans verticaux et d'un système son 5.1 permettant aux phonèmes de naviguer dans cinq hauts parleurs. Les fréquences basses font frémir et bruire le sol écorché. Aucun traitement n'est appliqué sur les photographies ({' '}
                    <a id="link" href="https://www.2angles.org/" target="_blank" rel="noopener noreferrer">
                      © Nicolas Brasseur
                    </a>
                    ) ; des gélatines colorées ont été utilisées pour filtrer la lumière et envelopper l'espace d'un bleu éthéré qui invite le visiteur dans un autre plan.
                  </p>
                </div>

                {/* Écran de projection pour la section 1 */}
                <div 
                  id="screen" 
                  style={{
                    visibility: screenVisible ? 'visible' : 'hidden',
                    opacity: screenVisible ? 1 : 0
                  }}
                >
                  <img 
                    className="screen-projection"
                    src="/media/phonemesdeuxtablettegauchegifun.gif" 
                    alt="Left tablet projection"
                    style={{
                      visibility: activeScreen === 'left-tablet' ? 'visible' : 'hidden',
                      opacity: activeScreen === 'left-tablet' ? 1 : 0
                    }}
                  />
                  <img 
                    className="screen-projection-television"
                    src="/media/Phonemesdeuxtelevisiongifun.gif" 
                    alt="Television projection"
                    style={{
                      visibility: activeScreen === 'television' ? 'visible' : 'hidden',
                      opacity: activeScreen === 'television' ? 1 : 0
                    }}
                  />
                  <img 
                    className="screen-projection"
                    src="/media/phonemesdeuxecrangifdeux.gif" 
                    alt="Big screen projection"
                    style={{
                      visibility: activeScreen === 'big-screen' ? 'visible' : 'hidden',
                      opacity: activeScreen === 'big-screen' ? 1 : 0
                    }}
                  />
                  <img 
                    className="screen-projection"
                    src="/media/phonemesdeuxtablettedroitegifquatre.gif" 
                    alt="Right tablet projection"
                    style={{
                      visibility: activeScreen === 'right-tablet' ? 'visible' : 'hidden',
                      opacity: activeScreen === 'right-tablet' ? 1 : 0
                    }}
                  />
                </div>
              </div>

              {/* Bloc photo avec zones interactives */}
              <div className="first-grid-bloc">
                
                {/* Zones cliquables des écrans */}
                <div 
                  id="first-grid-left-tablet"
                  className="screen-area"
                  onMouseEnter={() => handleScreenHover('left-tablet', true)}
                  onMouseLeave={() => handleScreenHover('left-tablet', false)}
                />
                <div 
                  id="first-grid-television"
                  className="screen-area"
                  onMouseEnter={() => handleScreenHover('television', true)}
                  onMouseLeave={() => handleScreenHover('television', false)}
                />
                <div 
                  id="first-grid-big-screen"
                  className="screen-area"
                  onMouseEnter={() => handleScreenHover('big-screen', true)}
                  onMouseLeave={() => handleScreenHover('big-screen', false)}
                />
                <div 
                  id="first-grid-right-tablet"
                  className="screen-area"
                  onMouseEnter={() => handleScreenHover('right-tablet', true)}
                  onMouseLeave={() => handleScreenHover('right-tablet', false)}
                />

                {/* Contrôles audio (speakers) AMÉLIORÉS */}
                <button
                  id="left-speaker"
                  className={`speaker ${speakerStates.leftSpeaker ? 'active' : ''}`}
                  onClick={() => toggleSpeaker('leftSpeaker')}
                  title="Speaker gauche - Voix principale"
                />
                <button
                  id="roof-speaker"
                  className={`speaker ${speakerStates.roofSpeaker ? 'active' : ''}`}
                  onClick={() => toggleSpeaker('roofSpeaker')}
                  title="Speaker plafond - Harmoniques"
                />
                <button
                  id="right-speaker"
                  className={`speaker ${speakerStates.rightSpeaker ? 'active' : ''}`}
                  onClick={() => toggleSpeaker('rightSpeaker')}
                  title="Speaker droit - Percussions"
                />
                <button
                  id="ground-speaker"
                  className={`speaker ${speakerStates.groundSpeaker ? 'active' : ''}`}
                  onClick={() => toggleSpeaker('groundSpeaker')}
                  title="Speaker sol - Basses"
                />
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <div className="second-grid">
              
              <div className="second-grid-bloc">
                {/* Projections interactives de la section 2 */}
                <img 
                  src="/media/phonemesdeuxecrangifun.gif" 
                  id="second-grid-big-screen-projection"
                  alt="Second grid big screen"
                />
                <img 
                  src="/media/phonemesdeuxtablettedroitegifun.gif" 
                  id="second-grid-tablet-projection"
                  alt="Second grid tablet"
                />
                
                {/* Speaker de la section 2 */}
                <button
                  id="second-grid-speaker"
                  className={`speaker ${speakerStates.secondGridSpeaker ? 'active' : ''}`}
                  onClick={() => toggleSpeaker('secondGridSpeaker')}
                  title="Speaker section 2 - Ambiances"
                />
              </div>
              
              <div className="second-grid-bloc">
                <img 
                  src="/media/Phonemesdeuxtelevisiongifsix.gif" 
                  id="second-grid-television-projection"
                  alt="Second grid television"
                />
              </div>
            </div>
          </section>
        </div>
      )}
      </div>
    </>
  );
};

export default Phonemes2;